import { Box, Typography } from "@mui/material";
import { memo, useCallback, useLayoutEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IEvaluationForm } from "../../../models/form-satisfaction";
import Button from "../../UI/Button";
import { IFormSucceededProps } from "./models";

const translationPath = "components.formSucceeded.";

const getShowLinkConfig = (evaluationForm: IEvaluationForm) => ({
  description: evaluationForm.showLinkDescription,
  buttonLabel: evaluationForm.showLinkButtonLabel,
  link: evaluationForm.showLinkLink,
  onEnd: evaluationForm.showLinkOnEnd,
  greaterThen: evaluationForm.showLinkGreaterThen,
});

export const FormSucceeded = memo(
  ({
    onGoToStart,
    onResetInitialStep,
    evaluationForm,
    answers,
  }: IFormSucceededProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const redirectUserHandler = useCallback(() => {
      navigate("/");
    }, [navigate]);

    const lowestGrade = useMemo(() => {
      if (!answers || answers.length === 0) return 0;

      const grades = answers
        .map((a) => parseInt(a.answer, 10))
        .filter((n) => !isNaN(n));

      return grades.length > 0 ? Math.min(...grades) : 0;
    }, [answers]);

    const engagementConfig = useMemo(() => {
      const showLink = getShowLinkConfig(evaluationForm);
      const meetsGradeRequirement =
        showLink.greaterThen !== undefined && showLink.greaterThen !== null
          ? lowestGrade >= showLink.greaterThen
          : true;

      return {
        shouldShow: !!showLink.onEnd && meetsGradeRequirement,
        description: showLink.description,
        buttonLabel: showLink.buttonLabel,
        link: showLink.link,
      };
    }, [evaluationForm, lowestGrade]);

    const openEngagementLinkHandler = useCallback(() => {
      if (!engagementConfig.link) return;
      window.open(engagementConfig.link, "_blank", "noopener,noreferrer");
    }, [engagementConfig.link]);

    useLayoutEffect(() => {
      onResetInitialStep();
    }, [onResetInitialStep]);

    return (
      <Box
        sx={{
          px: 4,
          width: "100%",
          height: "calc(100vh - 15rem)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 4,
        }}
      >
        <img
          src="/form-success.png"
          style={{ maxHeight: 180 }}
          alt="form succeeded illustration"
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            textAlign: "center",
            width: "100%",
            maxWidth: 760,
          }}
        >
          <Typography variant="h4" fontWeight="bold">
            {t(translationPath + "title")}
          </Typography>
          {engagementConfig.shouldShow && (
            <Box
              sx={{
                mt: 2,
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 3,
                flexWrap: { xs: "wrap", md: "nowrap" },
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                {engagementConfig.description}
              </Typography>
              <Button
                variant="contained"
                onClick={openEngagementLinkHandler}
                sx={{
                  minWidth: { xs: "100%", md: 320 },
                  minHeight: 56,
                  px: 4,
                }}
              >
                {engagementConfig.buttonLabel}
              </Button>
            </Box>
          )}
          <Typography
            textAlign="center"
            sx={(theme) => ({
              mt: engagementConfig.shouldShow ? 5 : 0,
              color:
                theme.palette.grey[theme.palette.mode === "light" ? 600 : 400],
            })}
          >
            {t(translationPath + "subtitle")}
          </Typography>
        </Box>
        <Box
          sx={{
            width: "fit-content",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Button variant="contained" onClick={redirectUserHandler}>
            {t(translationPath + "button")}
          </Button>
          <Button variant="text" onClick={onGoToStart}>
            {t(translationPath + "view_answers")}
          </Button>
        </Box>
      </Box>
    );
  },
);
